/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Stack, Box, Typography, Paper, Container, TextField, Button, IconButton } from "@mui/material";
import { Add, Edit, Error } from '@mui/icons-material';
import Grid from '@mui/material/Unstable_Grid2';
import { useState, useEffect, Dispatch, SetStateAction, ChangeEvent, } from "react";

import { useAppBarHeight, useDebounce } from '../../../common/CustomHooks';
import { useTranslation } from 'react-i18next';
import 'react-toastify/dist/ReactToastify.css';
import { useParams } from 'react-router-dom';
import { DataGrid, GridCellParams, GridColDef, GridToolbar, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { stripedDateGrid } from '../../../common/CustumEmotionStyles';
import { remToPixels } from '../../../common/CustomFunctions';
import ActorBusinessLogic from '../../../businessLogic/ActorBusinessLogic';
import { IActorGuiModel } from '../../../model/IActorGuiModel';
import DialogActorAddEdit from '../components/DialogActorAddEdit';
import { digiAccActorType } from '../../../common/TextTypes';

const textBoxInputStyleActor = (theme: MaterialTheme) => css({
    backgroundColor: theme.palette.background.paper,
    fontWeight: "bold",
    //padding: theme.spacing(1.5),
    ".MuiOutlinedInput-input": { fontSize: "1rem" },
})

interface IProps {
    type: digiAccActorType
}

interface IToolbarProps {
    type: digiAccActorType,
    search: string,
    handleSearch: (search: string) => void,
    handleDialogActorPostOpen: () => void,
}

const CustomToolbar = (props: IToolbarProps): JSX.Element => {

    const { type, search, handleSearch, handleDialogActorPostOpen } = props;

    const { t, i18n } = useTranslation();
    const theme = useTheme();

    return <Stack direction="row" justifyContent="space-between" spacing={4} margin={theme.spacing(3)}>
        <Typography variant="h5">{type === "Customer" ? t("basic.customers") : t("basic.suppliers")}</Typography>
        <Stack direction="row" spacing={4}>
            <TextField
                css={textBoxInputStyleActor(theme)}
                size="small"
                variant="outlined"
                label={t("search.search")}
                color="warning"
                value={search}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)} />
            <Button variant="contained" onClick={handleDialogActorPostOpen}>
                {type === "Customer" ? t("basic.addCustomer") : t("basic.addSupplier")}
            </Button>
        </Stack>
    </Stack>
}

const Actors = (props: IProps): JSX.Element => {

    const { type } = props;

    const { organizationId } = useParams();
    const { t, i18n } = useTranslation();

    const theme = useTheme();

    const [openDialogActorPost, setOpenDialogActorPost] = useState(false);
    const handleDialogActorPostOpen = () => {
        setOpenDialogActorPost(true);
    }
    const handleDialogActorPostClose = () => {
        getActors(true);
        setOpenDialogActorPost(false);
    }

    const [chosenActor, setChosenActor] = useState<IActorGuiModel | null>(null);
    const [openDialogActorPut, setOpenDialogActorPut] = useState(false);
    const handleDialogActorPutOpen = (chosenActor: IActorGuiModel) => {
        setChosenActor(chosenActor);
        setOpenDialogActorPut(true)
    }
    const handleDialogActorPutClose = () => {
        setChosenActor(null);
        getActors(true);
        setOpenDialogActorPut(false)
    }

    //##################Datagrid params
    const [actors, setActors] = useState<IActorGuiModel[]>([]);
    const [actorsPageNum, setActorsPageNum] = useState(0);
    const [actorsLimitPerPage, setActorsLimitPerPage] = useState(100);
    const [actorsTotalPages, setActorsTotalPages] = useState(0);
    const [actorsTotalItems, setActorsTotalItems] = useState(0);

    const [actorsError, setActorsError] = useState<string[]>([]);

    const [search, setSearch] = useState("");
    const handleSearch = (search: string) => {
        setSearch(search);
    }
    const debouncedSearch = useDebounce(search, 700);

    const [loader, setLoader] = useState(false);

    const getActors = async (isMounted: boolean) => {

        const logic = new ActorBusinessLogic();

        setLoader(true);
        const result = await logic.getActors(
            organizationId ? organizationId : "",
            props.type === "Customer" ? "Customer" : "Supplier",
            search,
            actorsPageNum,
            actorsLimitPerPage
        );

        if (!result.hasErrors) {
            if (isMounted) {
                setActors(result.value);
                setActorsTotalPages(result.totalPages);
                setActorsTotalItems(result.totalItems);
            }
        } else {
            setActorsError(result.errors);
        }
        setLoader(false);
    }
    //###############################################################################################################################################
    //########################################################API Calls and useEffects###############################################################
    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        
        getActors(isMounted);

        return () => { isMounted = false };
    }, [organizationId, props.type, debouncedSearch, actorsLimitPerPage, actorsPageNum]);

    //###############################################################################################################################################
    //###############################################################################################################################################
    
    //###############################################################################################################################################
    //###############################################################################################################################################
    const appBarHeight = useAppBarHeight();
    //########################################################################################
    //########################################################################################

    const columns: GridColDef[] = [
        { field: props.type === "Customer" ? 'customerId' : 'supplierId', headerName: 'id', width: 60, hide: true, },
        { field: 'name', headerName: t('basic.name'), flex: 1,minWidth: 200, },
        { field: 'addressLine1', headerName: t('basic.addressLine1'), width: 200, },
        { field: 'addressLine2', headerName: t('basic.addressLine2'), width: 200, },
        { field: 'isoCountryCode', headerName: t('basic.country'), width: 120, },
        { field: 'postArea', headerName: t('basic.postArea'), width: 120, },
        { field: 'postalCode', headerName: t('basic.postalCode'), width: 100, },
        { field: 'businessNumber', headerName: t('basic.businessNumber'), width: 240, },
        { field: 'emailAddress', headerName: t('basic.email'), width: 200, },
        {
            field: 'edit', headerName: "", width: 60, renderCell: (param: GridCellParams) => (
                <IconButton><Edit /></IconButton>
            ) },
    ];

    function CustomErrorOverlay() {
        return <Grid container direction="column" justifyContent="center" alignItems="center" height="100%">
            <Grid><Error fontSize="large" /></Grid>
            <Grid width="15rem" display="flex" justifyContent="center">
                <Typography variant="body2">
                    {t("basic.unexpectedError")}
                </Typography>
            </Grid>
        </Grid>
    }

    const handlePageSizeChange = (newPageSize: number) => {
        setActorsLimitPerPage(newPageSize);
    };

    return <main>
        <Grid container component={Paper} direction="column" wrap="nowrap"
            sx={{
                padding: theme.spacing(2),
                width: "100%",
                height: {
                    xs: "calc(100vh - " + appBarHeight + "px )",
                    xl: "calc(100vh)"
                },
            }}>
            <DataGrid css={stripedDateGrid(theme, theme.palette.grey[200])}
                onRowClick={(rowData) => handleDialogActorPutOpen(rowData.row)}
                getRowId={(row) => props.type === "Customer" ? row.customerId : row.supplierId }
                rows={actors}
                columns={columns}
                rowHeight={remToPixels(2)}
                pagination
                paginationMode="server"
                pageSize={actorsLimitPerPage}
                rowCount={actorsTotalItems}
                loading={loader}
                onPageChange={(newPage) => {
                    console.log('Page changed to:', newPage);
                    setActorsPageNum(newPage);
                    console.log('actorsPageNum updated to:', newPage);
                }}
                onPageSizeChange={handlePageSizeChange}
                localeText={{
                    toolbarColumns: t("muiDatagrid.columns"),
                    toolbarColumnsLabel: t("muiDatagrid.columnsLabel"),
                    columnsPanelTextFieldPlaceholder: t("muiDatagrid.columnTitle"),
                    columnsPanelTextFieldLabel: t("muiDatagrid.columnFind"),
                    columnsPanelShowAllButton: t("muiDatagrid.showAll"),
                    columnsPanelHideAllButton: t("muiDatagrid.hideAll"),

                    toolbarExport: t("muiDatagrid.export"),
                    toolbarExportLabel: t("muiDatagrid.exportLabel"),
                    toolbarExportCSV: t("muiDatagrid.exportCSV"),
                    toolbarExportPrint: t("muiDatagrid.exportPrint"),
                    toolbarExportExcel: t("muiDatagrid.exportExcel"),
                }}
                headerHeight={remToPixels(4)}
                getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                }
                error={actorsError.length > 0 ? true : undefined}
                components={{
                    ErrorOverlay: CustomErrorOverlay,
                    Toolbar: CustomToolbar,
                }}
                componentsProps={{
                    toolbar: {
                        type,
                        search,
                        handleSearch,
                        handleDialogActorPostOpen,
                    }
                }} />
            {openDialogActorPost && < DialogActorAddEdit
                open={openDialogActorPost}
                closeDialog={handleDialogActorPostClose}
                actorType={props.type}
                actor={undefined} />}
            {chosenActor && openDialogActorPut && <DialogActorAddEdit
                open={openDialogActorPut}
                closeDialog={handleDialogActorPutClose}
                actorType={props.type}
                actor={chosenActor} />}
        </Grid>
    </main>

}
export default Actors;